// @flow
// vendor libs
import * as Cookie from 'app/packages/cookie-utils';
import BrowserDetector from 'app/common/lib/BrowserDetector';
import BrowserType from 'app/packages/browser';
import changeWindowLocation from 'app/common/lib/util/changeWindowLocation';

const logToServerLoader = () =>
  import(/* webpackChunkName: "LogToServerCommon" */ 'app/common/lib/LogToServer');

let isBrowserExtensionInstalled;

const BROWSER_EXTENSION_INSTALLED_ATTRIBUTE = 'data-pinterest-extension-installed';

const BROWSER_EXTENSION_SET_CONTEXT_TIMEOUT = 100;

const BrowserExtension = {};

const BrowserExtensionData = {};

BrowserExtensionData[BrowserType.CHROME] = {
  url: PCONST.BrowserExtensionUrls.EXTENSION_PINIT_CHROME_URL,
  newWindow: true,
};

BrowserExtensionData[BrowserType.FIREFOX] = {
  url: PCONST.BrowserExtensionUrls.EXTENSION_PINIT_FIREFOX_URL,
  newWindow: false,
};

BrowserExtensionData[BrowserType.SAFARI] = {
  url: PCONST.BrowserExtensionUrls.EXTENSION_PINIT_SAFARI_URL,
  newWindow: true,
};

BrowserExtensionData[BrowserType.IE] = {
  url: PCONST.BrowserExtensionUrls.EXTENSION_PINIT_IE_URL,
  newWindow: false,
};

BrowserExtensionData[BrowserType.OPERA] = {
  url: PCONST.BrowserExtensionUrls.EXTENSION_PINIT_OPERA_URL,
  newWindow: true,
};

/**
 * Whether to use a new window to open the browser extension url.
 */
function _openInNewWindow() {
  const browser = BrowserDetector.getBrowser();
  if (browser) {
    return BrowserExtensionData[browser].newWindow;
  } else {
    return undefined;
  }
}

/**
 * Get extension installation url for desired browser.
 */
function _getBrowserExtensionUrl() {
  const browser = BrowserDetector.getBrowser();
  if (browser) {
    return BrowserExtensionData[browser].url;
  } else {
    return undefined;
  }
}

/**
 * Handle installation of the Pinterest extension
 *
 * @param {function(...*):*} onInstallClick
 */
BrowserExtension.handleExtensionInstall = (onInstallClick: () => void) => {
  const browserExtensionUrl = _getBrowserExtensionUrl();
  if (browserExtensionUrl) {
    changeWindowLocation(browserExtensionUrl, _openInNewWindow());
    onInstallClick();
  }
};

/**
 * Whether the Pinterest installation is currently installed.
 *   true if the installation is installed
 *   false if the extension is not installed.
 *   'undefined' if we do not yet know the state of the browser extension
 */
BrowserExtension.isExtensionInstalled = () => isBrowserExtensionInstalled;

/**
 * Whether the browser type is Chrome.
 */
BrowserExtension.isChrome = () => BrowserDetector.getBrowser() === BrowserType.CHROME;

/**
 * Detects if the browser extension is installed or not and publishes
 * the detected state. You should test and modify the attributeCheckDelay
 * depending your feature. 100ms likely will NOT work.
 * Ask @kdavison for more info. I also made an exp to easily test this:
 * https://analytics.pinadmin.com/experiments/test_ext_timeout/experimentor/
 * You'll have to pass the experiment group to extensionReady.
 * See usage here: https://phabricator.pinadmin.com/D241886
 */
BrowserExtension.extensionReady = (
  attributeCheckDelay: number = BROWSER_EXTENSION_SET_CONTEXT_TIMEOUT,
): Promise<any> =>
  new Promise((resolve) => {
    setTimeout(() => {
      if (!document.body) {
        return;
      }
      // N.B. this is intentionally accessing a global variable
      isBrowserExtensionInstalled = document.body.hasAttribute(
        BROWSER_EXTENSION_INSTALLED_ATTRIBUTE,
      );
      Cookie.set(PCONST.COOKIE_BROWSER_EXTENSION_INSTALLED, isBrowserExtensionInstalled.toString());

      // Don't show browser extension install prompts if it's an unrecognized browser
      if (BrowserDetector.getBrowser() === BrowserType.OTHER) {
        isBrowserExtensionInstalled = true;
      }

      resolve(isBrowserExtensionInstalled);
    }, attributeCheckDelay);
  });

BrowserExtension.initBrowserExtension = () => {
  logToServerLoader().then(({ default: LogToServer }) => {
    LogToServer.logToServer(
      LogToServer.SERVER_LOG_TYPE.BROWSER_EXTENSION,
      LogToServer.HTTP_METHOD.POST,
      {
        browser_extension_installed: isBrowserExtensionInstalled,
      },
    );
  });
};

export default BrowserExtension;
